import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Grid, TextField, Typography, Popover, Button, Checkbox, FormControlLabel } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteField from '../../otherComponent/DeleteStep';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import Reportform from '../../Reportform';

interface InputFieldProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
  handleChange: (data: any) => void;
  handleBlur: (data: any) => void;
  selectedNodeData:any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function InputField({ values, setFieldValue, index, onSubmit, handleChange, handleBlur, selectedNodeData, onDeleteField, toSentenceCase, fieldTypeSelector, isSettingsOpen, onSettingsOpen, onSettingsClose }: InputFieldProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-label-popover' : undefined; 
  const field = values.fields[index];
  const settings = field.settings || {};
  const isThisFieldUnique = settings.isUniqueCode;
  const otherUniqueExists = values.fields.some((f: any, i: number) => f.settings?.isUniqueCode && i !== index);
  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ flexGrow: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(field.question || 'Untitled Field')}
              </Typography>
              {settings.isAuthKey && <VpnKeyIcon fontSize="small" color="action" />}

              <TextSnippetIcon fontSize="small" sx={{ color: 'gray' }} />
            </Box>
            

           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                ref={settingsButtonRef}
                sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex', alignItems: 'center' }}
                onClick={handleEditClick}
              >
                <SettingsIcon fontSize="medium" />
              </Box>
             <DeleteField 
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
            <Typography variant="subtitle1">Text Settings</Typography>
            {fieldTypeSelector ? (
              <Box sx={{ minWidth: 120 }}>
                {fieldTypeSelector}
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>

              {/* change the onchange to give free hand to user to put . or ? mark as well in the inputtype text */}
             <TextField
              fullWidth
              label="Label"
              name={`fields.${index}.question`}
              defaultValue={field.question}
              onChange={(e) => setFieldValue(`fields.${index}.question`, e.target.value)}
              onBlur={handleBlur}
              size="small"
            />
            </Grid> 

            <Grid item xs={12} sx={{ mt: -1 }}>
              <TextField
                fullWidth
                label="Description"
                name={`fields.${index}.description`}
                defaultValue={field.description}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Min-Length"
                name={`fields.${index}.settings.min`}
                type="number"
                defaultValue={settings.min || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid> */}

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Max-Length"
                name={`fields.${index}.settings.max`}
                type="number"
                defaultValue={settings.max || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid> */}

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="RegExp"
                name={`fields.${index}.settings.regexp`}
                defaultValue={settings.regexp || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid> */}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.required || false}
                    onChange={(event) => setFieldValue(`fields.${index}.settings.required`, event.target.checked)}
                  />
                }
                label="Required"
              />
            </Grid>
           <Grid item xs={12} sx={{ mt: -3 }}>
             <FormControlLabel
               control={
                 <Checkbox
                   checked={settings.isMultiline || false}
                   onChange={(event) => setFieldValue(`fields.${index}.settings.isMultiline`, event.target.checked)}
                 />
               }
               label="Bigger Text Area"
             />
           </Grid>

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.isAuthkey}
                    onChange={(event) => {
                      const isChecked = event.target.checked;
                      setFieldValue(`fields.${index}.settings.isAuthkey`, isChecked);
                      if (isChecked) {
                        setFieldValue(`fields.${index}.settings.required`, true);
                        setFieldValue(`fields.${index}.settings.isUniqueCode`, false);
                      }
                    }}
                  />
                }
                label="Auth Key 🔑"
              />
            </Grid>

            {/* <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.isSupportFileRequired || false}
                    onChange={(event) => setFieldValue(`fields.${index}.settings.isSupportFileRequired`, event.target.checked)}
                  />
                }
                label="Support File Required"
              />
            </Grid> */}

            {settings.isSupportFileRequired && (  
              <Grid item xs={12} sx={{ mt: -3 }}>
                <TextField
                  fullWidth
                  label="Number of Images"
                  name={`fields.${index}.settings.noOfSupportImages`}
                  type="number"
                  value={settings.noOfSupportImages || ''}
                  onChange={handleChange}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sx={{ mt: -3 }}>
              {!otherUniqueExists || isThisFieldUnique ? (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isThisFieldUnique}
                        onChange={(event) => {
                          const isChecked = event.target.checked;
                          setFieldValue(`fields.${index}.settings.isUniqueCode`, isChecked);

                          if (isChecked) {
                            setFieldValue(`fields.${index}.settings.required`, true);

                            setFieldValue(`fields.${index}.settings.isAuthkey`, false);
                          }
                        }}
                      />
                    }
                    label="Unique Code"
                  />
                </Grid>
              ) : null}
            </Grid>

            <Grid item xs={12}sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.multiple || false}
                    onChange={(event) => setFieldValue(`fields.${index}.settings.multiple`, event.target.checked)}
                  />
                }
                label="Multiple Entries"
              />
            </Grid>

            <Grid item xs={12}sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.isCounter || false}
                    onChange={(event: any) => setFieldValue(`fields.${index}.settings.isCounter`, event.target.checked)}
                  />
                }
                label="Counter"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.isPickupAddress || false}
                    onChange={(event) => setFieldValue(`fields.${index}.settings.isPickupAddress`, event.target.checked)}
                  />
                }
                label="Pickup Address"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.isDestinationAddress || false}
                    onChange={(event) => setFieldValue(`fields.${index}.settings.isDestinationAddress`, event.target.checked)}
                  />
                }
                label="Destination Address"
              />
            </Grid>


            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.report?.enabled || false}
                    onChange={(event) => {
                      const isChecked = event.target.checked;
                      // Unticking clears the mapping too, so a stale report
                      // can't linger invisibly on the field.
                      setFieldValue(
                        `fields.${index}.settings.report`,
                        isChecked
                          ? { ...(settings.report || {}), enabled: true }
                          : { enabled: false, uid: null, name: null }
                      );
                    }}
                  />
                }
                label="Report "
              />
            </Grid>

            {settings.report?.enabled && (
              <Grid item xs={12} sx={{ mt: -2 }}>
                <Reportform name={`fields.${index}.settings.report`} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button type="submit" size="small" variant="contained" onClick={handleClosePopover}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Box>
  );
}
